import React, { useRef, useEffect, useMemo } from "react";
import { attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Container, Graphics, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle, TextMetrics } from "pixi.js";
import { useTranslation } from "react-i18next";

import { useDispatch, useSelector } from "react-redux";

import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import ButtonShadow from "./ButtonShadow";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings, FONT_CONFIG } from "../../../../pixiSettings";
import { outlineShadow } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { setBet, closeMenu, closeSymbolPopup } from "../../../../State/actions";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import { triggerScalePress } from "../../../../Hooks/useButtonHover";
import {
  selectBets,
  selectBet,
  selectBalance,
  selectCriticalAnimationPlaying,
  selectIsSpinning,
  selectAutoPlayActive,
  selectAnyFeatureTriggeredOrActive,
  selectFreeRoundsActive,
  selectReplaySessionActive,
} from "../../../../State/selectors";

// Max bet button configuration - matches UI button dimensions
const MAX_BET_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  // An iPad upright (layout TABLET_PORTRAIT on the MOBILE device, 2026-10-29):
  // slightly bigger, as in the platform's own games — its places are in its
  // own size, so the gaps grow with it.
  LAYOUT_CONFIGS: {
    // TABLET_PORTRAIT: { deviceMultiplier: 2.14 },
    TABLET_PORTRAIT: { deviceMultiplier: 2.18 }, // a tiny bit larger
  },
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.03
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.575
      }
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.04
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.45
      }
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.1
      }
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0
    }
  }
};

// Max bet button configuration for desktop - different dimensions
const MAX_BET_BUTTON_CONFIG_DESKTOP = {
  BASE_WIDTH: 93,
  BASE_HEIGHT: 64,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 0.9
    }
  }
};

const ButtonMaxBet = ({ toolbar }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  const { getResponsiveConfig, device, orientation, dimensions, isMobileSmall } =
    useDimensions();

  // Get bets and balance from Redux
  const bets = useSelector(selectBets);
  const bet = useSelector(selectBet);
  const balance = useSelector(selectBalance);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const isSpinning = useSelector(selectIsSpinning);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const anyFeatureTriggeredOrActive = useSelector(selectAnyFeatureTriggeredOrActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const replaySessionActive = useSelector(selectReplaySessionActive);

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);
  const buttonContainerRef = useRef(null);

  // Calculate if button should be disabled - prevent during spins, critical animations, hold and win, autoplay, free rounds, or when already at max bet
  const isDisabled = useMemo(() => {
    // Disable during critical game states
    if (
      criticalAnimationPlaying ||
      isSpinning ||
      anyFeatureTriggeredOrActive ||
      autoPlayActive ||
      freeRoundsActive ||
      replaySessionActive
    ) {
      return true;
    }

    // Disable if current bet is already the maximum affordable bet
    const affordableBets = bets.filter((betValue) => betValue <= balance);
    if (affordableBets.length > 0) {
      const maxBet = Math.max(...affordableBets);
      return bet >= maxBet; // Already at max bet
    }

    return true; // No affordable bets available
  }, [
    criticalAnimationPlaying,
    isSpinning,
    anyFeatureTriggeredOrActive,
    autoPlayActive,
    freeRoundsActive,
    replaySessionActive,
    bet,
    bets,
    balance,
  ]);

  // Shared hover/press hook
  const { buttonRef, handlePointerOver, handlePointerOut, cleanupFilters, alpha, cursor } =
    useButtonBrightness({ disabled: isDisabled });


  const texture = getNewUITexture(
    device === PixiSettings.DEVICE.DESKTOP
      ? "icon_max_bet_landscape"
      : "icon_autoplay_background"
  );

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () =>
      device === PixiSettings.DEVICE.DESKTOP
        ? getResponsiveConfig(MAX_BET_BUTTON_CONFIG_DESKTOP, true)
        : getResponsiveConfig(MAX_BET_BUTTON_CONFIG, true),
    [device, getResponsiveConfig]
  );

  const maxBetLabel = useMemo(() => t("max_btn").toUpperCase(), [t]);

  // Calculate ideal font size based on button size — snap to even for clean rasterization
  const dpr = window.devicePixelRatio || 1;
  const idealFontSize = useMemo(() => {
    const buttonSize = Math.min(buttonConfig.width, buttonConfig.height);
    return Math.round((buttonSize * 0.38) / 2) * 2;
  }, [buttonConfig.width, buttonConfig.height]);

  // Calculate max width for text (with padding)
  const maxTextWidth = useMemo(() => {
    return buttonConfig.width * 0.7;
  }, [buttonConfig.width]);

  // Auto-scaling font size calculation (checks both width AND height)
  const maxTextHeight = useMemo(() => buttonConfig.height * 0.7, [buttonConfig.height]);

  const finalFontSize = useMemo(() => {
    let fontSize = idealFontSize;

    const measure = (size) => {
      const s = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight: "600",
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
        breakWords: false,
      });
      return TextMetrics.measureText(maxBetLabel, s);
    };

    // Width pass
    let metrics = measure(fontSize);
    if (metrics.width > maxTextWidth) {
      fontSize = Math.round((fontSize * (maxTextWidth / metrics.width)) / 2) * 2;
    }

    // Height pass — shrink until wrapped text fits in button
    metrics = measure(fontSize);
    if (metrics.height > maxTextHeight) {
      fontSize = Math.round((fontSize * (maxTextHeight / metrics.height)) / 2) * 2;
    }

    return fontSize;
  }, [maxBetLabel, idealFontSize, maxTextWidth, maxTextHeight]);

  // Text style for "MAX" label — white, bold, centered, responsive
  const maxTextStyle = useMemo(() => {
    return new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: finalFontSize,
      fontWeight: "600",
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: maxTextWidth,
      breakWords: false,
      resolution: dpr,
      // ...TEXT_STYLE_CONFIG.OUTLINE,
      ...outlineShadow(finalFontSize), // shadow scales with the text
    });
  }, [finalFontSize, maxTextWidth, dpr]);

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar.layout.maxBet.x from hook for X position
  const buttonPosition = useMemo(() => {
    // Get X position from toolbar layout if available, otherwise fallback to old logic
    const containerX = toolbar.layout?.maxBet?.x ?? (() => {
      if (isMobileSmall) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return buttonConfig.width / 1.575;
        } else {
          return toolbar.width - buttonConfig.width * 0.6;
        }
      } else if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return buttonConfig.width / 1.7;
        } else {
          return toolbar.width - buttonConfig.width * 0.325;
        }
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return buttonConfig.width * 2.0;
        } else {
          return toolbar.width - buttonConfig.width * 0.875;
        }
      } else {
        return buttonConfig.width * 9.;
      }
    })();

    // Calculate Y position based on device and orientation
    let containerY;
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        containerY = -toolbar.visualHeight - buttonConfig.height / 2 - buttonConfig.height * 0.1;
      } else {
        containerY = -dimensions.height / 2 - buttonConfig.height * 1.85;
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        containerY = -toolbar.visualHeight - buttonConfig.height / 2 - buttonConfig.height * 0.115;
      } else {
        containerY = -dimensions.height / 2 - buttonConfig.height * 1.8;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        containerY = -dimensions.height / 5.2 + buttonConfig.height / 2;
      } else {
        // Landscape: center the button vertically in the toolbar
        containerY = toolbar.height / 2;
      }
    } else {
      containerY = toolbar.height / 2;
    }

    return {
      x: containerX,
      y: containerY
    };
  }, [
    device,
    orientation,
    isMobileSmall,
    dimensions.height,
    toolbar.width,
    toolbar.height,
    toolbar.visualHeight,
    toolbar.layout?.maxBet?.x,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  const handleClick = () => {
    // Disable button during critical animations or spinning
    if (isDisabled) {
      return;
    }

    // Close menu if open
    dispatch(closeMenu());
    dispatch(closeSymbolPopup());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Press animation on entire container (sprite + text animate together)
    if (buttonContainerRef.current) {
      triggerScalePress(buttonContainerRef.current);
    }

    // Find the highest bet that doesn't exceed balance
    const affordableBets = bets.filter((bet) => bet <= balance);

    if (affordableBets.length > 0) {
      const maxBet = Math.max(...affordableBets);
      dispatch(setBet(maxBet));
    }

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Remove hover effects when button becomes disabled
  useEffect(() => {
    if (isDisabled && buttonRef.current) {
      buttonRef.current.filters = [];    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabled]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupFilters();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [cleanupFilters]);

  // Determine if button uses circular texture (mobile/tablet — not desktop)
  const isCircularButton = device !== PixiSettings.DEVICE.DESKTOP;

  // Calculate button radius for circular hitbox (only used when isCircularButton is true) - memoized
  const buttonRadius = useMemo(() => buttonConfig.width / 2, [buttonConfig.width]);

  // Don't render MaxBet button on tablet landscape
  if (
    device === PixiSettings.DEVICE.TABLET &&
    orientation === PixiSettings.ORIENTATION.LANDSCAPE
  ) {
    return null;
  }

  return (
    <Container
      ref={buttonContainerRef}
      name="secondary_button_max_bet"
      x={buttonPosition.x}
      y={buttonPosition.y}
    >
      {/* Interactive area - handles all pointer events */}
      <Graphics
        name="button_max_bet_interactive_area"
        eventMode="static"
        cursor={cursor}
        hitArea={
          isCircularButton
            ? new PIXI.Circle(0, 0, buttonRadius)
            : new PIXI.Rectangle(
                -buttonConfig.width / 2,
                -buttonConfig.height / 2,
                buttonConfig.width,
                buttonConfig.height
              )
        }
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          if (isCircularButton) {
            g.drawCircle(0, 0, buttonRadius);
          } else {
            g.drawRect(
              -buttonConfig.width / 2,
              -buttonConfig.height / 2,
              buttonConfig.width,
              buttonConfig.height
            );
          }
          g.endFill();
        }}
      />

      {/* Visual background */}
      {/* Soft drop shadow under it (ButtonShadow, utils/buttonShadow) */}
      <ButtonShadow target={buttonRef} texture={texture} width={buttonConfig.width} height={buttonConfig.height} />
      <Sprite
        ref={buttonRef}
        texture={texture}
        name="button_max_bet_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        roundPixels={true}
        x={0}
        y={0}
        eventMode="none"
        alpha={alpha}
      />

      {/* Localized "MAX" text on top */}
      <Text
        text={maxBetLabel}
        style={maxTextStyle}
        anchor={0.5}
        roundPixels={true}
        x={0}
        y={0}
        eventMode="none"
        alpha={alpha}
      />
    </Container>
  );
};

export default ButtonMaxBet;
